/**
 * Environment Indicator Component
 *
 * Displays the current environment (Local, Development, Production) and auth mode
 * so developers can see, at a glance, which configuration is active (FR-008).
 */

import type { Environment, AuthMode } from '../config/types';
import { useTheme, spacing } from '@fti/design-system';

interface EnvIndicatorProps {
  /** Current deployment environment. */
  environment: Environment;
  /** Current authentication mode. */
  authMode: AuthMode;
  /** Optionally, the active backend target key (shown when it differs from the default). */
  activeApiKey?: string;
}

const ENV_LABEL: Record<Environment, string> = {
  local: 'Local',
  dev: 'Development',
  prod: 'Production',
};

/**
 * Environment indicator badge with color coding via FTI Design System semantic colors:
 * - Local: Info (blue-teal); Development: Warning (amber); Production: Error (red).
 */
export function EnvIndicator({ environment, authMode, activeApiKey }: EnvIndicatorProps): JSX.Element {
  const { theme } = useTheme();

  const colors = (() => {
    switch (environment) {
      case 'local':
        return { background: theme.colors.semantic.info.light, border: theme.colors.semantic.info.default };
      case 'dev':
        return {
          background: theme.colors.semantic.warning.light,
          border: theme.colors.semantic.warning.default,
        };
      case 'prod':
        return { background: theme.colors.semantic.error.light, border: theme.colors.semantic.error.default };
      default:
        return { background: theme.colors.neutral[100], border: theme.colors.neutral[300] };
    }
  })();

  const base = `${ENV_LABEL[environment] ?? environment}${authMode === 'mock' ? ' (Mock)' : ''}`;
  const text = activeApiKey ? `${base} · ${activeApiKey}` : base;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: `${spacing.xs} ${spacing.sm}`,
        borderRadius: '0.375rem',
        background: colors.background,
        border: `1px solid ${colors.border}`,
        fontSize: '0.8rem',
        fontWeight: 500,
        color: theme.colors.neutral[900],
      }}
      title={`Environment: ${ENV_LABEL[environment] ?? environment} | Auth: ${authMode}${activeApiKey ? ` | API: ${activeApiKey}` : ''}`}
    >
      {text}
    </div>
  );
}
